// 3D Generation Page Logic
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { loadAuthenticatedMedia } from './utils/authenticated-media-loader.js';
import {
  getStoredTransformSource,
  clearStoredTransformSource,
} from './utils/transform-source.js';

// Type definitions for image slots
type ImageSlotKey =
  | 'primary'
  | 'extra1'
  | 'extra2'
  | 'extra3'
  | 'back'
  | 'left'
  | 'right';

// Global state
const inputImages: Map<ImageSlotKey, File> = new Map();
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;
let renderer: THREE.WebGLRenderer | null = null;
let controls: OrbitControls | null = null;
let loadedModel: THREE.Group | null = null;
let wireframeMode = false;
let currentModel: string = 'firtoz/trellis';

export function init3DGenerationPage() {
  // Check if modelUrl parameter is present (viewing existing model)
  const urlParams = new URLSearchParams(window.location.search);
  const modelUrl = urlParams.get('modelUrl');

  if (modelUrl) {
    // Direct viewer mode - hide upload/config UI, show viewer
    const uploadCard = document.getElementById('uploadCard');
    const configCard = document.getElementById('configCard');
    const resultsCard = document.getElementById('resultsCard');

    if (uploadCard) uploadCard.style.display = 'none';
    if (configCard) configCard.style.display = 'none';
    if (resultsCard) {
      resultsCard.style.display = 'block';
      // Load the model directly
      void initThreeJSViewer(modelUrl);

      // Hide metadata section since we don't have that info
      const metadataSection = document.getElementById('resultMetadata');
      if (metadataSection) metadataSection.style.display = 'none';

      // Set download link
      const downloadLink = document.getElementById(
        'downloadGLB'
      ) as HTMLAnchorElement;
      if (downloadLink) {
        downloadLink.href = modelUrl;
        downloadLink.download = 'model.glb';
      }
    }

    // Still bind viewer controls even in direct mode
    const resetCameraBtn = document.getElementById('resetCameraBtn');
    const toggleWireframeBtn = document.getElementById('toggleWireframeBtn');
    resetCameraBtn?.addEventListener('click', resetCamera);
    toggleWireframeBtn?.addEventListener('click', toggleWireframe);

    return; // Skip normal initialization
  }

  // Normal initialization for upload/generation mode
  // Check for sessionStorage data (from detail page re-transform)
  const stored = getStoredTransformSource();

  // Get DOM elements
  const modelSelect = document.getElementById('model') as HTMLSelectElement;
  const imageUploadArea = document.getElementById('imageUploadArea');
  const imageInput = document.getElementById('imageInput') as HTMLInputElement;
  const removeImageBtn = document.getElementById('removeImage');
  const generateForm = document.getElementById('generateForm');
  const newGenerationBtn = document.getElementById('newGenerationBtn');
  const resetCameraBtn = document.getElementById('resetCameraBtn');
  const toggleWireframeBtn = document.getElementById('toggleWireframeBtn');

  // Initialize model UI state
  currentModel = modelSelect?.value || 'firtoz/trellis';
  updateMultiImageUI(currentModel);

  // Model selector change handler
  modelSelect?.addEventListener('change', (event) => {
    currentModel = (event.target as HTMLSelectElement).value;
    updateMultiImageUI(currentModel);
    // Clear extra images when switching models (but keep primary)
    clearExtraImages();
  });

  // Load image from sessionStorage if present
  if (stored) {
    void loadImageFromUrl(stored.source!, stored.sourceType);

    // Clear sessionStorage after loading
    clearStoredTransformSource();
  }

  // Primary upload area click handler
  imageUploadArea?.addEventListener('click', () => imageInput.click());

  // Primary file input handler
  imageInput?.addEventListener('change', (e) =>
    handleImageUpload(e, 'primary')
  );

  // Primary remove button
  removeImageBtn?.addEventListener('click', () => removeImage('primary'));

  // Setup TRELLIS extra image slots
  setupExtraImageSlot('extra1', 'extraUploadArea1', 'extraInput1');
  setupExtraImageSlot('extra2', 'extraUploadArea2', 'extraInput2');
  setupExtraImageSlot('extra3', 'extraUploadArea3', 'extraInput3');

  // Setup Hunyuan multi-view slots
  setupExtraImageSlot('back', 'backUploadArea', 'backInput');
  setupExtraImageSlot('left', 'leftUploadArea', 'leftInput');
  setupExtraImageSlot('right', 'rightUploadArea', 'rightInput');

  // Form submit
  generateForm?.addEventListener('submit', handleSubmit);

  // New generation
  newGenerationBtn?.addEventListener('click', resetPage);

  // 3D viewer controls
  resetCameraBtn?.addEventListener('click', resetCamera);
  toggleWireframeBtn?.addEventListener('click', toggleWireframe);
}

// Setup click handler for an extra image slot
function setupExtraImageSlot(
  slot: ImageSlotKey,
  uploadAreaId: string,
  inputId: string
) {
  const uploadArea = document.getElementById(uploadAreaId);
  const input = document.getElementById(inputId) as HTMLInputElement;

  uploadArea?.addEventListener('click', () => input?.click());
  input?.addEventListener('change', (e) => handleImageUpload(e, slot));

  // Setup remove button
  const removeBtn = document.querySelector(
    `[data-slot="${slot}"].remove-btn-small`
  );
  removeBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    removeImage(slot);
  });
}

// Update UI based on selected model
function updateMultiImageUI(model: string) {
  const trellisSection = document.getElementById('trellisExtraImages');
  const hunyuanSection = document.getElementById('hunyuanMultiView');
  const primaryLabel = document.getElementById('primaryImageLabel');

  // Hide both by default
  if (trellisSection) trellisSection.style.display = 'none';
  if (hunyuanSection) hunyuanSection.style.display = 'none';

  // Update primary label and show appropriate section
  if (model === 'firtoz/trellis') {
    if (trellisSection) trellisSection.style.display = 'block';
    if (primaryLabel)
      primaryLabel.innerHTML =
        'Primaire Afbeelding <span class="required">*</span>';
  } else if (model === 'tencent/hunyuan3d-2mv') {
    if (hunyuanSection) hunyuanSection.style.display = 'block';
    if (primaryLabel)
      primaryLabel.innerHTML = 'Voorkant <span class="required">*</span>';
  } else {
    // Hunyuan3D-2: single image only
    if (primaryLabel)
      primaryLabel.innerHTML = 'Afbeelding <span class="required">*</span>';
  }
}

// Clear extra images (keep primary)
function clearExtraImages() {
  const extraSlots: ImageSlotKey[] = [
    'extra1',
    'extra2',
    'extra3',
    'back',
    'left',
    'right',
  ];
  for (const slot of extraSlots) {
    if (inputImages.has(slot)) {
      removeImage(slot);
    }
  }
}

// Function to load image from URL (from detail page re-transform)
async function loadImageFromUrl(url: string, sourceType: string = 'url') {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const filename = url.split('/').pop() || 'image.png';
    const file = new File([blob], filename, { type: blob.type });

    // Set as primary image
    inputImages.set('primary', file);

    // Show preview
    const previewImg = document.getElementById(
      'previewImg'
    ) as HTMLImageElement;
    const imagePreview = document.getElementById('imagePreview');
    const uploadArea = document.getElementById('imageUploadArea');

    if (previewImg) {
      previewImg.src = URL.createObjectURL(file);
      if (imagePreview) imagePreview.style.display = 'block';
      if (uploadArea) uploadArea.style.display = 'none';

      // Show config card
      const configCard = document.getElementById('configCard');
      if (configCard) configCard.style.display = 'block';
    }

    // Show success message with source type
    const sourceLabel =
      sourceType === 'source'
        ? 'bron afbeelding'
        : sourceType === 'result'
          ? 'result afbeelding'
          : sourceType === 'input'
            ? 'input afbeelding'
            : 'afbeelding';

    setTimeout(() => {
      showMessage(
        `${sourceLabel.charAt(0).toUpperCase() + sourceLabel.slice(1)} geladen uit geschiedenis`,
        'success'
      );
    }, 500);
  } catch (error) {
    console.error('Failed to load image from URL:', error);
    showMessage(
      'Kon afbeelding niet laden. Probeer handmatig uploaden.',
      'error'
    );
  }
}

// Expose to window for HTML initialization
declare global {
  interface Window {
    init3DGenerationPage: typeof init3DGenerationPage;
  }
}
window.init3DGenerationPage = init3DGenerationPage;

function handleImageUpload(event: Event, slot: ImageSlotKey) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  // Validate file type
  if (!file.type.startsWith('image/')) {
    showMessage('Alleen afbeeldingen zijn toegestaan', 'error');
    return;
  }

  // Validate file size (50MB)
  if (file.size > 50 * 1024 * 1024) {
    showMessage('Bestand te groot (max 50MB)', 'error');
    return;
  }

  inputImages.set(slot, file);

  // Show preview based on slot type
  if (slot === 'primary') {
    showPrimaryPreview(file);
  } else {
    showExtraPreview(file, slot);
  }
}

function showPrimaryPreview(file: File) {
  const reader = new FileReader();
  reader.onload = (e) => {
    const previewImg = document.getElementById(
      'previewImg'
    ) as HTMLImageElement;
    const imagePreview = document.getElementById('imagePreview');
    const uploadArea = document.getElementById('imageUploadArea');

    if (previewImg && e.target?.result) {
      previewImg.src = e.target.result as string;
      if (imagePreview) imagePreview.style.display = 'block';
      if (uploadArea) uploadArea.style.display = 'none';

      // Show config card
      const configCard = document.getElementById('configCard');
      if (configCard) configCard.style.display = 'block';
    }
  };
  reader.readAsDataURL(file);
}

function showExtraPreview(file: File, slot: ImageSlotKey) {
  const reader = new FileReader();
  reader.onload = (e) => {
    let previewImg: HTMLImageElement | null = null;
    let previewContainer: HTMLElement | null = null;
    let uploadArea: HTMLElement | null = null;

    // Map slot to DOM elements
    switch (slot) {
      case 'extra1':
        previewImg = document.getElementById(
          'extraPreviewImg1'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('extraPreview1');
        uploadArea = document.getElementById('extraUploadArea1');
        break;
      case 'extra2':
        previewImg = document.getElementById(
          'extraPreviewImg2'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('extraPreview2');
        uploadArea = document.getElementById('extraUploadArea2');
        break;
      case 'extra3':
        previewImg = document.getElementById(
          'extraPreviewImg3'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('extraPreview3');
        uploadArea = document.getElementById('extraUploadArea3');
        break;
      case 'back':
        previewImg = document.getElementById(
          'backPreviewImg'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('backPreview');
        uploadArea = document.getElementById('backUploadArea');
        break;
      case 'left':
        previewImg = document.getElementById(
          'leftPreviewImg'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('leftPreview');
        uploadArea = document.getElementById('leftUploadArea');
        break;
      case 'right':
        previewImg = document.getElementById(
          'rightPreviewImg'
        ) as HTMLImageElement;
        previewContainer = document.getElementById('rightPreview');
        uploadArea = document.getElementById('rightUploadArea');
        break;
    }

    if (previewImg && e.target?.result) {
      previewImg.src = e.target.result as string;
      if (previewContainer) previewContainer.style.display = 'flex';
      if (uploadArea) uploadArea.style.display = 'none';
    }
  };
  reader.readAsDataURL(file);
}

function removeImage(slot: ImageSlotKey) {
  inputImages.delete(slot);

  if (slot === 'primary') {
    const imagePreview = document.getElementById('imagePreview');
    const uploadArea = document.getElementById('imageUploadArea');
    const configCard = document.getElementById('configCard');

    if (imagePreview) imagePreview.style.display = 'none';
    if (uploadArea) uploadArea.style.display = 'flex';

    // Only hide config if no primary image
    if (!inputImages.has('primary') && configCard) {
      configCard.style.display = 'none';
    }
  } else {
    // Handle extra image removal
    let previewContainer: HTMLElement | null = null;
    let uploadArea: HTMLElement | null = null;

    switch (slot) {
      case 'extra1':
        previewContainer = document.getElementById('extraPreview1');
        uploadArea = document.getElementById('extraUploadArea1');
        break;
      case 'extra2':
        previewContainer = document.getElementById('extraPreview2');
        uploadArea = document.getElementById('extraUploadArea2');
        break;
      case 'extra3':
        previewContainer = document.getElementById('extraPreview3');
        uploadArea = document.getElementById('extraUploadArea3');
        break;
      case 'back':
        previewContainer = document.getElementById('backPreview');
        uploadArea = document.getElementById('backUploadArea');
        break;
      case 'left':
        previewContainer = document.getElementById('leftPreview');
        uploadArea = document.getElementById('leftUploadArea');
        break;
      case 'right':
        previewContainer = document.getElementById('rightPreview');
        uploadArea = document.getElementById('rightUploadArea');
        break;
    }

    if (previewContainer) previewContainer.style.display = 'none';
    if (uploadArea) uploadArea.style.display = 'flex';
  }
}

async function handleSubmit(event: Event) {
  event.preventDefault();

  if (!inputImages.has('primary')) {
    showMessage('Upload eerst een afbeelding', 'error');
    return;
  }

  // Get form values
  const modelSelect = document.getElementById('model') as HTMLSelectElement;
  const qualitySelect = document.getElementById(
    'qualityPreset'
  ) as HTMLSelectElement;
  const promptInput = document.getElementById('prompt') as HTMLTextAreaElement;

  const model = modelSelect.value;
  const qualityPreset = qualitySelect.value;
  const prompt = promptInput.value.trim();

  // Show loading
  const uploadCard = document.getElementById('uploadCard');
  const configCard = document.getElementById('configCard');
  const loadingCard = document.getElementById('loadingCard');
  const resultsCard = document.getElementById('resultsCard');

  if (uploadCard) uploadCard.style.display = 'none';
  if (configCard) configCard.style.display = 'none';
  if (loadingCard) loadingCard.style.display = 'block';
  if (resultsCard) resultsCard.style.display = 'none';

  // Simulate progress (since Replicate doesn't provide real progress)
  simulateProgress();

  try {
    // Build form data with multiple images
    const formData = new FormData();

    // Always add primary image
    formData.append('imageFile', inputImages.get('primary')!);
    formData.append('model', model);
    formData.append('qualityPreset', qualityPreset);
    if (prompt) formData.append('prompt', prompt);

    // Add model-specific extra images
    if (model === 'firtoz/trellis') {
      // TRELLIS: extra images as numbered files
      if (inputImages.has('extra1')) {
        formData.append('imageFile2', inputImages.get('extra1')!);
      }
      if (inputImages.has('extra2')) {
        formData.append('imageFile3', inputImages.get('extra2')!);
      }
      if (inputImages.has('extra3')) {
        formData.append('imageFile4', inputImages.get('extra3')!);
      }
    } else if (model === 'tencent/hunyuan3d-2mv') {
      // Hunyuan3D-2mv: named view images
      if (inputImages.has('back')) {
        formData.append('backImage', inputImages.get('back')!);
      }
      if (inputImages.has('left')) {
        formData.append('leftImage', inputImages.get('left')!);
      }
      if (inputImages.has('right')) {
        formData.append('rightImage', inputImages.get('right')!);
      }
    }

    // Call API using authenticatedFetch (from auth.js)
    const response = await (window as any).authenticatedFetch(
      '/api/image-to-3d',
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Generation failed');
    }

    const result = await response.json();

    // Hide loading, show results
    if (loadingCard) loadingCard.style.display = 'none';
    if (resultsCard) resultsCard.style.display = 'block';

    // Display results
    displayResults(result);

    showMessage('3D model succesvol gegenereerd!', 'success');
  } catch (error) {
    console.error('Generation failed:', error);
    if (loadingCard) loadingCard.style.display = 'none';
    if (uploadCard) uploadCard.style.display = 'block';
    if (configCard) configCard.style.display = 'block';
    showMessage(
      error instanceof Error ? error.message : 'Generatie mislukt',
      'error'
    );
  }
}

function simulateProgress() {
  const progressBar = document.getElementById('progressBar');
  if (!progressBar) return;

  let progress = 0;
  const interval = setInterval(() => {
    progress += Math.random() * 10;
    if (progress > 90) progress = 90; // Stop at 90% until real completion
    progressBar.style.width = `${progress}%`;
  }, 500);

  // Store interval ID to clear later
  (progressBar as any).progressInterval = interval;
}

function displayResults(result: any) {
  // Clear progress interval
  const progressBar = document.getElementById('progressBar');
  if (progressBar && (progressBar as any).progressInterval) {
    clearInterval((progressBar as any).progressInterval);
    progressBar.style.width = '100%';
  }

  // Initialize Three.js viewer
  void initThreeJSViewer(result.glbUrl);

  // Display preview renders if available
  if (result.renders && result.renders.length > 0) {
    const previewRenders = document.getElementById('previewRenders');
    const renderGrid = document.getElementById('renderGrid');

    if (previewRenders) previewRenders.style.display = 'block';
    if (renderGrid) {
      renderGrid.innerHTML = '';

      result.renders.forEach((renderUrl: string) => {
        const img = document.createElement('img');
        img.src = renderUrl;
        img.alt = 'Preview Render';
        img.className = 'render-preview';
        renderGrid.appendChild(img);
      });
    }
  }

  // Display metadata
  const metadata = result.metadata;
  const resultModel = document.getElementById('resultModel');
  const resultQuality = document.getElementById('resultQuality');
  const resultTextureSize = document.getElementById('resultTextureSize');
  const resultFileSize = document.getElementById('resultFileSize');
  const resultTime = document.getElementById('resultTime');
  const resultCost = document.getElementById('resultCost');

  if (resultModel) resultModel.textContent = metadata.modelName;
  if (resultQuality) resultQuality.textContent = metadata.qualityPreset;
  if (resultTextureSize)
    resultTextureSize.textContent = `${metadata.textureSize}px`;
  if (resultFileSize)
    resultFileSize.textContent = `${(metadata.fileSizeBytes / 1024 / 1024).toFixed(2)} MB`;
  if (resultTime)
    resultTime.textContent = `${(metadata.generationTime / 1000).toFixed(1)}s`;
  if (resultCost) resultCost.textContent = `$${metadata.cost.toFixed(4)}`;

  // Set download link
  const downloadLink = document.getElementById(
    'downloadGLB'
  ) as HTMLAnchorElement;
  if (downloadLink) {
    downloadLink.href = result.glbUrl;
    downloadLink.download = `model_${result.modelId}.glb`;
  }
}

async function initThreeJSViewer(glbUrl: string) {
  const canvas = document.getElementById('threejs-canvas') as HTMLCanvasElement;

  // Setup scene
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf5f5f5);

  // Setup camera
  camera = new THREE.PerspectiveCamera(
    50,
    canvas.clientWidth / canvas.clientHeight,
    0.1,
    1000
  );
  camera.position.set(3, 3, 3);

  // Setup renderer
  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    alpha: true,
  });
  renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.5; // Increased for brighter rendering
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  // Setup controls
  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.screenSpacePanning = false;
  controls.minDistance = 1;
  controls.maxDistance = 10;

  // Add lights - improved for better visibility
  // Ambient light for overall scene illumination
  const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
  scene.add(ambientLight);

  // Key light (main directional light)
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.5);
  keyLight.position.set(5, 8, 5);
  keyLight.castShadow = true;
  scene.add(keyLight);

  // Fill light (softer, from opposite side)
  const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
  fillLight.position.set(-5, 3, -5);
  scene.add(fillLight);

  // Back light (rim light for depth)
  const backLight = new THREE.DirectionalLight(0xffffff, 0.6);
  backLight.position.set(0, 3, -8);
  scene.add(backLight);

  // Hemisphere light for natural sky/ground lighting
  const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.5);
  hemiLight.position.set(0, 20, 0);
  scene.add(hemiLight);

  // Add ground plane
  const groundGeometry = new THREE.PlaneGeometry(10, 10);
  const groundMaterial = new THREE.ShadowMaterial({ opacity: 0.1 });
  const ground = new THREE.Mesh(groundGeometry, groundMaterial);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0;
  ground.receiveShadow = true;
  scene.add(ground);

  // Load GLB model with authentication using centralized utility
  const loader = new GLTFLoader();

  try {
    const blobUrl = await loadAuthenticatedMedia(glbUrl);

    loader.load(
      blobUrl,
      (gltf: GLTF) => {
        // Clean up blob URL after loading
        URL.revokeObjectURL(blobUrl);
        loadedModel = gltf.scene;

        // Center and scale model
        const box = new THREE.Box3().setFromObject(loadedModel);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Center model
        loadedModel.position.sub(center);

        // Scale to fit
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        loadedModel.scale.setScalar(scale);

        // Enable shadows and fix materials/textures
        loadedModel.traverse((child: THREE.Object3D) => {
          if ((child as THREE.Mesh).isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;

            // Fix materials to properly display textures
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
              // Handle both single material and material arrays
              const materials = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material];

              materials.forEach((mat: any) => {
                if (mat.map) {
                  mat.map.colorSpace = THREE.SRGBColorSpace;
                  mat.needsUpdate = true;
                }
                mat.side = THREE.DoubleSide;
              });
            }
          }
        });

        // Add to scene
        scene!.add(loadedModel);
        console.log('3D model loaded successfully');

        // Enable controls
        const resetCameraBtn = document.getElementById('resetCameraBtn');
        const toggleWireframeBtn =
          document.getElementById('toggleWireframeBtn');
        if (resetCameraBtn) resetCameraBtn.style.display = 'inline-block';
        if (toggleWireframeBtn)
          toggleWireframeBtn.style.display = 'inline-block';

        // Position camera to view model
        resetCamera();
      },
      undefined,
      (error: unknown) => {
        console.error('Error loading GLB:', error);
        showMessage('Fout bij laden van 3D model', 'error');
      }
    );
  } catch (error) {
    console.error('Error loading authenticated GLB:', error);
    showMessage('Fout bij laden van 3D model', 'error');
  }

  // Animation loop
  animate();

  // Handle window resize
  window.addEventListener('resize', onWindowResize);
}

function animate() {
  requestAnimationFrame(animate);

  if (controls) controls.update();
  if (renderer && scene && camera) {
    renderer.render(scene, camera);
  }
}

function onWindowResize() {
  const canvas = document.getElementById('threejs-canvas') as HTMLCanvasElement;

  if (camera && renderer) {
    camera.aspect = canvas.clientWidth / canvas.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);
  }
}

function resetCamera() {
  if (!camera || !controls || !loadedModel) return;

  // Calculate optimal camera position
  const box = new THREE.Box3().setFromObject(loadedModel);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());

  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
  cameraZ *= 1.5; // Add margin

  camera.position.set(cameraZ, cameraZ, cameraZ);
  camera.lookAt(center);

  controls.target.copy(center);
  controls.update();
}

function toggleWireframe() {
  if (!loadedModel) return;

  wireframeMode = !wireframeMode;

  loadedModel.traverse((child: THREE.Object3D) => {
    if ((child as THREE.Mesh).isMesh) {
      const mesh = child as THREE.Mesh;
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((mat: any) => {
          mat.wireframe = wireframeMode;
        });
      } else {
        (mesh.material as any).wireframe = wireframeMode;
      }
    }
  });

  const btn = document.getElementById('toggleWireframeBtn');
  if (btn) {
    btn.textContent = wireframeMode ? 'Solid' : 'Wireframe';
  }
}

function resetPage() {
  // Clean up Three.js
  if (renderer) {
    renderer.dispose();
    renderer = null;
  }
  if (scene) {
    scene.clear();
    scene = null;
  }
  camera = null;
  controls = null;
  loadedModel = null;

  // Clear all images
  inputImages.clear();

  // Reset primary image DOM
  const imagePreview = document.getElementById('imagePreview');
  const uploadArea = document.getElementById('imageUploadArea');
  if (imagePreview) imagePreview.style.display = 'none';
  if (uploadArea) uploadArea.style.display = 'flex';

  // Reset extra images DOM
  clearExtraImagesDOM();

  // Reset cards visibility
  const uploadCard = document.getElementById('uploadCard');
  const configCard = document.getElementById('configCard');
  const resultsCard = document.getElementById('resultsCard');
  const loadingCard = document.getElementById('loadingCard');

  if (uploadCard) uploadCard.style.display = 'block';
  if (configCard) configCard.style.display = 'none';
  if (resultsCard) resultsCard.style.display = 'none';
  if (loadingCard) loadingCard.style.display = 'none';

  // Clear form
  const form = document.getElementById('generateForm') as HTMLFormElement;
  if (form) form.reset();

  // Reset model selector and UI
  const modelSelect = document.getElementById('model') as HTMLSelectElement;
  if (modelSelect) {
    currentModel = modelSelect.value;
    updateMultiImageUI(currentModel);
  }
}

function clearExtraImagesDOM() {
  const extraSlots = [
    { preview: 'extraPreview1', upload: 'extraUploadArea1' },
    { preview: 'extraPreview2', upload: 'extraUploadArea2' },
    { preview: 'extraPreview3', upload: 'extraUploadArea3' },
    { preview: 'backPreview', upload: 'backUploadArea' },
    { preview: 'leftPreview', upload: 'leftUploadArea' },
    { preview: 'rightPreview', upload: 'rightUploadArea' },
  ];

  for (const slot of extraSlots) {
    const preview = document.getElementById(slot.preview);
    const upload = document.getElementById(slot.upload);
    if (preview) preview.style.display = 'none';
    if (upload) upload.style.display = 'flex';
  }
}

function showMessage(message: string, type: 'success' | 'error') {
  const messageEl = document.getElementById('message');
  if (!messageEl) return;

  messageEl.textContent = message;
  messageEl.className = type === 'success' ? 'success' : 'error';
  messageEl.style.display = 'block';

  setTimeout(() => {
    messageEl.style.display = 'none';
  }, 5000);
}
